import React, { useMemo } from "react";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "antd/es/select";
import { ColumnType } from "antd/es/table";
import { IntlShape } from "react-intl";

import ImportMapping from "@mapmycustomers/shared/types/imports/ImportMapping";
import { Alert, SelectField } from "@mapmycustomers/ui";

import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import MatchedInformation from "@app/component/MatchedInformation";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import { setMappingField } from "@app/scene/settings/component/Imports/store/actions";
import ImportMappingOption from "@app/scene/settings/component/Imports/type/ImportMappingOption";
import ImportMappingTableRow from "@app/scene/settings/component/Imports/type/ImportMappingTableRow";
import { MAPPING_SKIP_KEY } from "@app/scene/settings/component/Imports/util/consts";

import styles from "./FieldMappingStep.module.scss";

const getOptionName = (intl: IntlShape, mappingOption: ImportMappingOption): string => {
  let name = mappingOption.label || "";
  if (mappingOption.skip) {
    name = intl.formatMessage({
      id: "settings.newImports.matchFieldsStep.option.skip",
      defaultMessage: "SKIP",
      description: "Skip column text",
    });
  } else if (mappingOption.importId) {
    name = intl.formatMessage({
      id: "settings.newImports.matchFieldsStep.option.uniqueId",
      defaultMessage: "Unique ID",
      description: "Unique ID column text",
    });
  }
  return `${name}${mappingOption.required ? " *" : ""}`;
};

const useMappingTableConfig = (
  intl: IntlShape,
  mapOptions: ImportMappingOption[],
  mappedColumns: ImportMapping,
  onSetMappingField: typeof setMappingField,
  onShowMmcFieldInfoModal: () => void
): ColumnType<ImportMappingTableRow>[] =>
  useMemo(
    () => [
      {
        dataIndex: "fileField",
        key: "fileField",
        render: (fileField, importMappingTableRow: ImportMappingTableRow) => {
          return (
            <>
              <span>{fileField}</span>
              {importMappingTableRow.isMmcFieldUnique && (
                <Alert
                  className={styles.alert}
                  message={intl.formatMessage(
                    {
                      id: "settings.newImports.matchFieldsStep.column.uniqueAlert",
                      defaultMessage: "Each value <b>must</b> be unique to sync.",
                      description: "Each value must be unique to sync alert message",
                    },
                    { b: (text) => <b>{text}</b> }
                  )}
                  showIcon
                  type="warning"
                />
              )}
            </>
          );
        },
        title: intl.formatMessage({
          id: "settings.newImports.matchFieldsStep.column.fileColumn",
          defaultMessage: "Column Header from File",
          description: "Title of first column on imports step 2",
        }),
      },
      {
        dataIndex: "mmcField",
        key: "mmcField",
        render: (mmcField, importMappingTableRow, index) => (
          <SelectField<string>
            autoFocus={index === 0}
            className={styles.mappingSelect}
            dropdownMatchSelectWidth={false}
            filterOption={defaultFilterOption}
            onChange={(mmcField: string) =>
              onSetMappingField({ fileField: importMappingTableRow.fileField, mmcField })
            }
            showSearch
            size="middle"
            value={mmcField}
          >
            {mapOptions.map((option, index) => (
              <Select.Option
                disabled={(!!mappedColumns[option.value!] || option.separator) && !option.skip}
                key={index}
                label={getOptionName(intl, option)}
                value={option.value!}
              >
                {getOptionName(intl, option)}
              </Select.Option>
            ))}
          </SelectField>
        ),
        title: intl.formatMessage(
          {
            id: "settings.newImports.matchFieldsStep.column.mmcColumn",
            defaultMessage: "Map My Customers Field {infoIcon}",
            description: "Title of second column on imports step 2",
          },
          {
            infoIcon: (
              <FontAwesomeIcon
                className={styles.infoIcon}
                icon={faInfoCircle}
                onClick={onShowMmcFieldInfoModal}
              />
            ),
          }
        ),
      },
      {
        dataIndex: "preview",
        key: "preview",
        title: (
          <TextWithInfo
            info={intl.formatMessage({
              id: "settings.newImports.matchFieldsStep.column.preview.tooltip",
              defaultMessage:
                "This is a preview of the first row of data found in the selected column",
              description: "Tooltip of preview column on imports matching step",
            })}
          >
            {intl.formatMessage({
              id: "settings.newImports.matchFieldsStep.column.preview",
              defaultMessage: "Preview",
              description: "Title of third column on imports matching step",
            })}
          </TextWithInfo>
        ),
      },
      {
        dataIndex: "mmcField",
        key: "matched",
        render: (mmcField) => (
          <MatchedInformation matched={!!mmcField && mmcField !== MAPPING_SKIP_KEY} />
        ),
        title: intl.formatMessage({
          id: "settings.newImports.matchFieldsStep.column.matched",
          defaultMessage: "Status",
          description: "Title of fourth column on imports matching step",
        }),
      },
    ],
    [intl, mapOptions, mappedColumns, onSetMappingField, onShowMmcFieldInfoModal]
  );

export default useMappingTableConfig;
